// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import { Action } from "redux"
import { UnsetActivityTypeRequest, UnsetActivityTypeResponse } from "generated/wisetime/user/timeline/v4/timeline_pb"
import * as Actions from "../actions"
import { grpc } from "app/rpc/executor"
import { timelineClient } from "app/rpc/client"
import { isGrpcError } from "../../../lib/rpcutils"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type UnsetActivityTypeRpc = (request: UnsetActivityTypeRequest) => Observable<UnsetActivityTypeResponse>

export const unsetActivityType = (unsetActivityTypeRpc: UnsetActivityTypeRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.unsetActivityType.match),
      mergeMap(action => of(action.payload)
        .pipe(
          mergeMap(unsetActivityTypeRpc),
          // TODO: Consider not loading entire timeline. Can we simply update the local state?
          mergeMap(_ => of(Actions.getCurrentTimelineData())),
          catchError(handleError),
        )
      ),
    )

const unsetActivityTypeRpc = (request: UnsetActivityTypeRequest): Observable<UnsetActivityTypeResponse> =>
  grpc(() => timelineClient.unsetActivityType(request, null))

const handleError = (e: unknown): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_unset_activity_type_failed_not_found", message: "Failed to remove activity type. Could not find the target time row." }),
          messageType: "ERROR",
        })),
        of(Actions.onTimelineOutOfSync()),
      )
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_unset_activity_type_failed_permission_denied", message: "Failed to remove activity type. You do not have permission to remove this activity type from the time row." }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_unset_activity_type_failed_precondition", message: "Failed to unset activity type. Time row may be out of sync." }),
          messageType: "ERROR",
        })),
        of(Actions.onTimelineOutOfSync()),
      )
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_unset_activity_type_failed", message: "Failed to remove activity type. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

export default unsetActivityType(unsetActivityTypeRpc)
